import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

import { Badge } from '../../components/ui';
import { OpportunityResultRow, ScoringState } from './api';
import OpportunityDetailPanel from './OpportunityDetailPanel';
import { displaySource, OpportunityStatus, opportunityById, statusLabel, STRATEGIC_LABELS } from './publicScoring';

interface OpportunityRankingTableProps {
  rows: OpportunityResultRow[];
  state: ScoringState;
  expandedId: string | null;
  onToggle: (id: string) => void;
  onScoreChange: (id: string, kind: 'gain' | 'pain', index: number, value: number) => void;
  onStatusChange: (id: string, status: OpportunityStatus) => void;
  onDetailFieldChange: (id: string, label: string, value: string) => void;
}

export default function OpportunityRankingTable({
  rows,
  state,
  expandedId,
  onToggle,
  onScoreChange,
  onStatusChange,
  onDetailFieldChange,
}: OpportunityRankingTableProps) {
  return (
    <section className="px-4 py-7">
      <div className="mx-auto max-w-7xl">
        <div className="mb-4 flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
          <div>
            <h2 className="font-subtitle text-h2 text-charcoal dark:text-gray-100">Opportunity Ranking</h2>
            <p className="text-small text-gray-600 dark:text-gray-300">
              Click any row to expand details and edit narrative fields or factor scores.
            </p>
          </div>
          <p className="text-small text-gray-500 dark:text-gray-400">Top 10 active opportunities are highlighted.</p>
        </div>
        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-dark-border dark:bg-dark-surface">
          <div className="overflow-x-auto">
            <table className="min-w-[1180px] divide-y divide-gray-200 text-small dark:divide-dark-border">
              <colgroup>
                <col className="w-[64px]" />
                <col className="w-[92px]" />
                <col className="w-[340px]" />
                <col className="w-[180px]" />
                <col className="w-[130px]" />
                <col className="w-[130px]" />
                <col className="w-[120px]" />
                <col className="w-[220px]" />
              </colgroup>
              <thead className="bg-[#1A2540] text-left text-micro uppercase tracking-wide text-white">
                <tr>
                  <th className="px-4 py-3">#</th>
                  <th className="px-4 py-3">ID</th>
                  <th className="px-4 py-3">Opportunity</th>
                  <th className="px-4 py-3">Theme</th>
                  <th className="px-4 py-3">Gain Index</th>
                  <th className="px-4 py-3">Pain Index</th>
                  <th className="px-4 py-3">Priority</th>
                  <th className="px-4 py-3">Source</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-dark-border">
                {rows.map((row) => {
                  const expanded = expandedId === row.id;
                  const opportunity = opportunityById(state, row.id);
                  return (
                    <FragmentRow
                      key={row.id}
                      row={row}
                      expanded={expanded}
                      state={state}
                      opportunity={opportunity}
                      onToggle={onToggle}
                      onScoreChange={onScoreChange}
                      onStatusChange={onStatusChange}
                      onDetailFieldChange={onDetailFieldChange}
                    />
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </section>
  );
}

function FragmentRow({
  row,
  expanded,
  state,
  opportunity,
  onToggle,
  onScoreChange,
  onStatusChange,
  onDetailFieldChange,
}: {
  row: OpportunityResultRow;
  expanded: boolean;
  state: ScoringState;
  opportunity: ReturnType<typeof opportunityById>;
  onToggle: (id: string) => void;
  onScoreChange: (id: string, kind: 'gain' | 'pain', index: number, value: number) => void;
  onStatusChange: (id: string, status: OpportunityStatus) => void;
  onDetailFieldChange: (id: string, label: string, value: string) => void;
}) {
  const detail = state.details?.[row.id];
  return (
    <>
      <tr
        id={`opportunity-${row.id}`}
        className={row.is_top_10_active ? 'bg-[#FFF1EA] dark:bg-eliza-red/10' : 'bg-white dark:bg-dark-surface'}
      >
        <td className="whitespace-nowrap px-4 py-3 align-top">
          <div className="flex items-center gap-2">
            <button
              type="button"
              aria-label={`${expanded ? 'Collapse' : 'Expand'} ${row.name}`}
              onClick={() => onToggle(row.id)}
              className="rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-charcoal dark:hover:bg-dark-surface-2"
            >
              {expanded ? <ChevronDownIcon className="h-4 w-4" /> : <ChevronRightIcon className="h-4 w-4" />}
            </button>
            <span className={`flex h-7 w-7 items-center justify-center rounded-full bg-[#F2967B] text-micro font-bold text-white ${
              row.is_top_10_active ? 'ring-2 ring-[#1A2540]' : ''
            }`}>
              {row.active_rank ?? row.rank ?? '—'}
            </span>
          </div>
        </td>
        <td className="px-4 py-3 align-top">
          <Badge variant="secondary">{row.id}</Badge>
        </td>
        <td className="min-w-[320px] px-4 py-3 align-top">
          <button
            type="button"
            onClick={() => onToggle(row.id)}
            className="text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-eliza-red"
          >
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="font-semibold text-charcoal hover:text-eliza-red dark:text-gray-100">{row.name}</span>
              <Badge variant={row.status === 'active' ? 'success' : 'secondary'}>{statusLabel(row.status)}</Badge>
              {(row.strategic_tags ?? []).map((tag) => (
                <Badge key={tag} variant="secondary">{STRATEGIC_LABELS[tag] ?? tag}</Badge>
              ))}
            </div>
          </button>
        </td>
        <td className="px-4 py-3 align-top text-gray-600 dark:text-gray-300">
          <div>{row.theme_name ?? '—'}</div>
          {row.theme_id !== undefined && <div className="mt-1 text-micro text-gray-400">T{row.theme_id}</div>}
        </td>
        <ScoreCell value={row.gain_score} tone="gain" />
        <ScoreCell value={row.pain_score} tone="pain" />
        <td className="px-4 py-3 align-top">
          <span
            className={`rounded px-2 py-1 font-semibold ${priorityClass(priorityValue(row))}`}
            title={row.priority_score_note ?? undefined}
          >
            {formatScore(priorityValue(row))}{row.priority_score_source === 'source_indexes' ? '*' : ''}
          </span>
        </td>
        <td className="px-4 py-3 align-top text-gray-600 dark:text-gray-300">
          <span className="block max-w-[210px] truncate" title={row.source ?? undefined}>
            {displaySource(row.source)}
          </span>
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={8} className="p-0">
            <OpportunityDetailPanel
              row={row}
              opportunity={opportunity}
              detail={detail}
              state={state}
              onScoreChange={onScoreChange}
              onStatusChange={onStatusChange}
              onDetailFieldChange={onDetailFieldChange}
            />
          </td>
        </tr>
      )}
    </>
  );
}

function ScoreCell({ value, tone }: { value: number; tone: 'gain' | 'pain' }) {
  const color = tone === 'gain' ? 'bg-eliza-red' : 'bg-slate-500';
  return (
    <td className="min-w-[120px] px-4 py-3 align-top">
      <div className="font-semibold text-charcoal dark:text-gray-100">{value}</div>
      <div className="mt-2 h-2 overflow-hidden rounded-full bg-gray-100 dark:bg-dark-surface-2">
        <div className={`h-full ${color}`} style={{ width: `${Math.max(0, Math.min(100, value))}%` }} />
      </div>
    </td>
  );
}

function priorityValue(row: OpportunityResultRow) {
  return row.priority_score ?? row.composite_score;
}

function priorityClass(value: number) {
  if (value >= 40) return 'bg-eliza-red text-white';
  if (value >= 20) return 'bg-orange-100 text-orange-900 dark:bg-orange-900/40 dark:text-orange-100';
  return 'bg-gray-100 text-gray-700 dark:bg-dark-surface-2 dark:text-gray-200';
}

function formatScore(value: number) {
  return Number.isInteger(value) ? String(value) : value.toFixed(2);
}
